<template>
  <div
    v-if="show"
    class="dialog-overlay"
    :class="variant ? `dialog-overlay--${variant}` : ''"
    @click.self="$emit('close')"
  >
    <div
      class="dialog"
      :class="variant ? `dialog--${variant}` : ''"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
    >
      <div class="dialog-header">
        <div class="dialog-heading">
          <slot name="header-leading"></slot>
          <div class="dialog-heading-copy">
            <h3 :id="titleId">{{ title }}</h3>
            <slot name="subtitle"></slot>
          </div>
        </div>
        <button type="button" class="close-btn" :aria-label="closeLabel" @click="$emit('close')">
          <svg aria-hidden="true" viewBox="0 0 16 16">
            <path d="M3.5 3.5l9 9m0-9l-9 9" />
          </svg>
        </button>
      </div>
      <div class="dialog-content">
        <slot></slot>
      </div>
      <div class="dialog-buttons">
        <slot name="buttons"></slot>
      </div>
    </div>
  </div>
</template>

<script>
let dialogInstanceId = 0

export default {
  name: 'DialogModal',
  data() {
    dialogInstanceId += 1
    return {
      titleId: `dialog-title-${dialogInstanceId}`
    }
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      required: true
    },
    variant: {
      type: String,
      default: ''
    },
    closeLabel: {
      type: String,
      default: 'Close'
    }
  },
  emits: ['close']
}
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--color-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  width: 90%;
  max-width: 500px;
  overflow: hidden;
  animation: dialog-appear 0.2s ease-out;
}

@keyframes dialog-appear {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.985);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--color-border);
}

.dialog-heading {
  display: flex;
  align-items: center;
  min-width: 0;
}

.dialog-heading-copy {
  min-width: 0;
}

.dialog-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: var(--color-text);
}

.close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  padding: 0;
  color: var(--color-text-muted);
  background: var(--color-surface-muted);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  font-size: 1.35rem;
  cursor: pointer;
  line-height: 1;
  transition: color 0.18s ease, background 0.18s ease, border-color 0.18s ease;
}

.close-btn:hover {
  color: var(--color-text);
  background: var(--color-surface);
  border-color: var(--color-border-strong);
}

.close-btn:focus-visible {
  outline: none;
  border-color: var(--signal-active-strong);
  box-shadow: var(--focus-ring-brand);
}

.close-btn svg {
  width: 15px;
  height: 15px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-width: 1.7;
}

@media (prefers-reduced-motion: reduce) {
  .dialog {
    animation: none;
  }
}

.dialog-content {
  padding: 1.5rem;
}

.dialog-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: var(--color-surface-muted);
  border-top: 1px solid var(--color-border);
}
</style>
